import type { JSX } from 'react';
import { useAppMode, type AppMode } from '@/lib/appMode';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { HeaderStatusPill } from './HeaderStatusPill';
import { SeedData } from '@/data/reports/seedData';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { useState } from 'react';

const MODE_CONFIG: Record<
  AppMode,
  {
    icon: Parameters<typeof HeaderStatusPill>[0]['icon'];
    label: string;
    description: string;
    tone: Parameters<typeof HeaderStatusPill>[0]['tone'];
  }
> = {
  demo: {
    icon: 'science',
    label: 'Demo',
    description: 'Stai usando dati di esempio. Le modifiche non influenzano i dati reali.',
    tone: 'warning',
  },
  real: {
    icon: 'database',
    label: 'Dati Reali',
    description: 'Stai usando il database di produzione. Le modifiche sono permanenti.',
    tone: 'info',
  },
};

/**
 * Mode indicator in the header showing whether app is in Demo or Real mode.
 * Clicking opens a popover with mode details and switch option.
 */
export function ModeIndicator(): JSX.Element {
  const { mode, switchToDemo, switchToReal } = useAppMode();
  const queryClient = useQueryClient();
  const [isResetting, setIsResetting] = useState(false);
  const [open, setOpen] = useState(false);

  // Default to demo if mode is null (shouldn't happen after setup)
  const currentMode = mode ?? 'demo';
  const config = MODE_CONFIG[currentMode];

  const handleSwitchMode = () => {
    setOpen(false);
    if (currentMode === 'demo') {
      switchToReal();
    } else {
      switchToDemo();
    }
    // Note: page will reload, so no need for additional feedback
  };

  const handleResetDemoData = async () => {
    setIsResetting(true);
    try {
      await SeedData.clearAndSeed();
      await queryClient.invalidateQueries();
      toast.success('Dati demo rigenerati');
      setOpen(false);
    } catch (error) {
      console.error('[ModeIndicator] Errore reset dati demo:', error);
      toast.error('Errore durante il reset dei dati demo');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderStatusPill
          tone={config.tone}
          icon={config.icon}
          label={config.label}
          ariaLabel={`Modalità: ${config.label}`}
        />
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-72">
        <div className="space-y-3">
          <div>
            <p className="font-medium">{config.label}</p>
            <p className="text-sm text-muted-foreground">{config.description}</p>
          </div>

          <div className="space-y-2">
            {currentMode === 'demo' && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleResetDemoData}
                disabled={isResetting}
              >
                {isResetting ? 'Rigenerazione...' : 'Rigenera dati demo'}
              </Button>
            )}

            <Button variant="secondary" size="sm" className="w-full" onClick={handleSwitchMode}>
              Passa a {currentMode === 'demo' ? 'Dati Reali' : 'Demo'}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            {currentMode === 'demo'
              ? 'I dati reali non verranno modificati passando alla modalità reale.'
              : "Passando alla modalità demo potrai esplorare l'app senza rischi."}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
